import React, { useState, useEffect } from "react";
import Customer, { CustomerType, CustomerViewModel } from "entities/customer";
import CustomerFilter from "entities/customerFilter";

export const PricesPage = () => {
    const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
    const [monthlyCustomers, setMonthlyCustomers] = useState<CustomerViewModel[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingPrice, setEditingPrice] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchCustomerTypes = async () => {
            try {
                setCustomerTypes(
                    await Customer.getCustomerTypes()
                );
                setMonthlyCustomers(
                    await Customer.search(new CustomerFilter({ onlyCurrent:true, onlyMonthly:true }))
                );
            } catch (error) {
                console.error("Error fetching customer types", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomerTypes();
    }, []);

    const formatPrice = (price: number) =>
        new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 0,
        }).format(price);

    const handleEditClick = (type: CustomerType) => {
        setEditingId(type.id);
        setEditingPrice(type.price.toString());
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingPrice("");
    };

    const handleSavePrice = async (customerTypeId: number) => {
        const newPrice = parseInt(editingPrice, 10);

        if (isNaN(newPrice) || newPrice <= 0) {
            alert("Por favor ingrese un precio válido");
            return;
        }

        setIsSaving(true);
        try {
            await Customer.updateCustomerTypePrice(customerTypeId, newPrice);

            setCustomerTypes((prevTypes: CustomerType[]) =>
                prevTypes.map((type: CustomerType) =>
                    type.id === customerTypeId ? { ...type, price: newPrice } : type
                )
            );

            setEditingId(null);
            setEditingPrice("");
        } catch (error) {
            console.error("Error updating price", error);
            alert("Error al actualizar el precio");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="container-fluid container-fluid-spacious">
            <div className="container pt-3">
                <div className="row card-deck">
                    <div className="card mb-4 box-shadow p-3">
                        <h2>Planes de membresías mensuales</h2>
                        {isLoading ? (
                            <div className="py-3">Cargando...</div>
                        ) : (
                            <div>
                                <table cellPadding={4}>
                                    {customerTypes
                                        .filter((type) => type.isRecurrent && !type.isGroup)
                                        .map((type) => (
                                            <tr key={type.id}>
                                                <td>{type.name}</td>
                                                <td>
                                                    {editingId === type.id ? (
                                                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                value={editingPrice}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingPrice(e.target.value)}
                                                                style={{ width: "120px" }}
                                                                disabled={isSaving}
                                                                autoFocus
                                                            />
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => handleSavePrice(type.id)}
                                                                disabled={isSaving}
                                                            >
                                                                {isSaving ? "Guardando..." : "Guardar"}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={handleCancelEdit}
                                                                disabled={isSaving}
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                            <span>
                                                                <strong style={{ fontSize: "larger" }}>{formatPrice(type.price)}</strong> ({formatPrice(type.price / 1.21)} + IVA)
                                                            </span>
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleEditClick(type)}
                                                            >
                                                                Editar
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </table>
                                <h3 style={{ marginTop: "24px" }}>Clientes con precios personalizados</h3>
                                {monthlyCustomers.filter((customer) => customer.contractPrice).length > 0 ? (
                                    <table cellPadding={4} style={{ width: "100%", marginTop: "12px" }}>
                                        <thead>
                                            <tr style={{ borderBottom: "2px solid #ddd" }}>
                                                <th style={{ textAlign: "left", padding: "8px" }}>Cliente</th>
                                                <th style={{ textAlign: "left", padding: "8px" }}>Precio personalizado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthlyCustomers
                                                .filter((customer) => customer.contractPrice)
                                                .map((customer) => (
                                                    <tr key={customer.id} style={{ borderBottom: "1px solid #eee" }}>
                                                        <td style={{ padding: "8px" }}>
                                                            <a
                                                                href={`/customers/edit/${customer.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: "#60a5fa", textDecoration: "none" }}
                                                                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                                                                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                                                            >
                                                                {customer.legalName}
                                                            </a>
                                                        </td>
                                                        <td style={{ padding: "8px" }}>
                                                            <strong>{customer.contractPrice ? formatPrice(customer.contractPrice) : "-"}</strong>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ marginTop: "12px", color: "#666", fontStyle: "italic" }}>
                                        No hay clientes con precios personalizados
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="card mb-4 box-shadow p-3">
                        <h2>Otros</h2>
                        <p>Sala de reuniones por hora: $4000</p>
                        <p>
                            <strong>No estamos tomando membresías diarias por el momento</strong> (sí ofrecemos que los interesados se queden a trabajar un día gratis para que conozcan el espacio)
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}; 